import React, { useState, useEffect } from 'react';
import Header from '../component/Header';
import BackgroundImage from '../images/tirvia/trivia-bg.png';
import { AiOutlineClockCircle } from 'react-icons/ai';
import { IoClose } from 'react-icons/io5';
import { MdDragIndicator } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { getToken } from '../utils/tokenHelper';

const HealthChainReaction = () => {
    const [showModal, setShowModal] = useState(true);
    const [quizStarted, setQuizStarted] = useState(false);
    const [timeLeft, setTimeLeft] = useState(90); // Changed from 180 to 90 seconds (1 min 30 secs)
    const [attemptsLeft, setAttemptsLeft] = useState(4);
    const [loading, setLoading] = useState(true);
    const [canAccess, setCanAccess] = useState(false);
    const [accessMessage, setAccessMessage] = useState('');
    const [quizConfig, setQuizConfig] = useState(null);
    const [batchId, setBatchId] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [puzzle, setPuzzle] = useState(null);
    const [steps, setSteps] = useState([]);
    const [startingQuiz, setStartingQuiz] = useState(false);
    const [submittingAnswer, setSubmittingAnswer] = useState(false);
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [completionMessage, setCompletionMessage] = useState('');
    const [showTimeoutModal, setShowTimeoutModal] = useState(false);
    const [timeExpired, setTimeExpired] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [draggedIndex, setDraggedIndex] = useState(null);
    const navigate = useNavigate();

    // Shuffle array function
    const shuffleArray = (array) => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    // Fetch quiz config on component mount with delay
    useEffect(() => {
        const fetchQuizConfig = async () => {
            // Add 5-second delay before fetching
            await new Promise(resolve => setTimeout(resolve, 5000));
            
            const token = getToken();
            if (!token) {
                console.error('No token found');
                setErrorMessage('Please login to play this game');
                setShowErrorModal(true);
                setTimeout(() => {
                    navigate('/trivia-games');
                }, 3000);
                setLoading(false);
                return;
            }

            const myHeaders = new Headers();
            myHeaders.append("Accept", "application/json");
            myHeaders.append("Authorization", `Bearer ${token}`);

            const requestOptions = {
                method: "GET",
                headers: myHeaders,
                redirect: "follow"
            };

            try {
                const response = await fetch("https://devapi.quickcare.ng/api/v1/games/hcr/config", requestOptions);
                
                if (!response.ok) {
                    throw new Error(`Server error: ${response.status}`);
                }
                
                const result = await response.json();

                if (result.success && result.data) {
                    setQuizConfig(result.data);
                    setCanAccess(result.data.can_access || false);
                    setAccessMessage(result.data.message || '');

                    if (typeof result.data.attempts === 'number') {
                        setAttemptsLeft(result.data.attempts);
                    }

                    // Override timer to 90 seconds (1 min 30 secs) regardless of API response
                    setTimeLeft(90);

                    if (typeof result.data.max_question === 'number') {
                        setTotalQuestions(result.data.max_question);
                    }
                } else {
                    throw new Error(result.message || 'Failed to load game configuration');
                }
            } catch (error) {
                console.error('Error fetching quiz config:', error);
                setErrorMessage(error.message || 'Failed to load game. Please try again later.');
                setShowErrorModal(true);
                setTimeout(() => {
                    navigate('/trivia-games');
                }, 3000);
            } finally {
                setLoading(false);
            }
        };

        fetchQuizConfig();
    }, [navigate]);

    // Shuffle steps when puzzle changes
    useEffect(() => {
        if (puzzle && puzzle.steps_to_order && puzzle.steps_to_order.length > 0) {
            setSteps(shuffleArray([...puzzle.steps_to_order]));
        }
    }, [puzzle]);

    // Timer countdown
    useEffect(() => {
        if (quizStarted && timeLeft > 0 && !timeExpired) {
            const timer = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        setTimeExpired(true);
                        setShowTimeoutModal(true);
                        setQuizStarted(false);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [quizStarted, timeLeft, timeExpired]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const handleDragStart = (index) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (dropIndex) => {
        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            return;
        }

        const newSteps = [...steps];
        const draggedItem = newSteps[draggedIndex];
        newSteps.splice(draggedIndex, 1);
        newSteps.splice(dropIndex, 0, draggedItem);
        setSteps(newSteps);
        setDraggedIndex(null);
    };

    const handleMoveUp = (index) => {
        if (index === 0) return;
        const newSteps = [...steps];
        [newSteps[index - 1], newSteps[index]] = [newSteps[index], newSteps[index - 1]];
        setSteps(newSteps);
    };

    const handleMoveDown = (index) => {
        if (index === steps.length - 1) return;
        const newSteps = [...steps];
        [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
        setSteps(newSteps);
    };

    const handleSubmit = async () => {
        if (!batchId || submittingAnswer) return;

        setSubmittingAnswer(true);

        try {
            const token = getToken();
            if (!token) {
                console.error('No token found');
                setSubmittingAnswer(false);
                return;
            }

            // Format answers array with step_id and submitted_order
            const answers = steps.map((step, index) => ({
                step_id: step.id,
                submitted_order: index + 1
            }));

            const myHeaders = new Headers();
            myHeaders.append("Content-Type", "application/json");
            myHeaders.append("Accept", "application/json");
            myHeaders.append("Authorization", `Bearer ${token}`);

            const raw = JSON.stringify({
                answers: answers,
                batch_id: batchId
            });

            const requestOptions = {
                method: "POST",
                headers: myHeaders,
                body: raw,
                redirect: "follow"
            };

            const response = await fetch("https://devapi.quickcare.ng/api/v1/games/hcr/answer", requestOptions);
            const result = await response.json();

            if (result.success) {
                // Check if the game is completed (current_index is 3 for the 4th question)
                // OR if the API returns null data with a completion message
                if (result.data === null && result.message.includes("game completed")) {
                    // Set custom completion message
                    setCompletionMessage('Answer submitted, Good Luck.');
                    setShowCompletionModal(true);
                    setQuizStarted(false);
                } else if (result.data) {
                    // Check if this is the last question (current_index is 3 for the 4th question)
                    const isLastQuestion = result.data.current_index >= 3;
                    
                    if (isLastQuestion) {
                        // This was the last question, next submission will complete the game
                        // Continue to show next question if available
                        if (result.data.puzzle) {
                            setPuzzle(result.data.puzzle);
                            setCurrentIndex(result.data.current_index);
                            setSteps([]);
                        }
                    } else {
                        // Move to next question
                        if (result.data.puzzle) {
                            setPuzzle(result.data.puzzle);
                            setCurrentIndex(result.data.current_index);
                            setSteps([]);
                        }
                    }
                }
            } else {
                // Handle API error
                setErrorMessage(result.message || 'Failed to submit answer. Please try again.');
                setShowErrorModal(true);
            }
        } catch (error) {
            console.error('Error submitting answer:', error);
            setErrorMessage('Network error. Please check your connection and try again.');
            setShowErrorModal(true);
        } finally {
            setSubmittingAnswer(false);
        }
    };

    const handlePlay = async () => {
        if (!canAccess) {
            return;
        }
        
        setStartingQuiz(true);
        
        try {
            const token = getToken();
            if (!token) {
                console.error('No token found');
                setStartingQuiz(false);
                return;
            }

            const myHeaders = new Headers();
            myHeaders.append("Accept", "application/json");
            myHeaders.append("Authorization", `Bearer ${token}`);

            const requestOptions = {
                method: "POST",
                headers: myHeaders,
                redirect: "follow"
            };

            const response = await fetch("https://devapi.quickcare.ng/api/v1/games/hcr/start", requestOptions);
            const result = await response.json();

            if (result.success && result.data) {
                setBatchId(result.data.batch_id);
                setSessionId(result.data.session_id);
                setCurrentIndex(result.data.current_index || 0);
                setTotalQuestions(result.data.total_questions || 4);
                
                if (result.data.puzzle) {
                    setPuzzle(result.data.puzzle);
                    if (result.data.steps_to_order) {
                        setSteps(shuffleArray([...result.data.steps_to_order]));
                    }
                }

                setShowModal(false);
                setQuizStarted(true);
                setTimeLeft(90); // Reset timer to 90 seconds
                setTimeExpired(false);
            } else if (!result.success) {
                // Handle error response
                setErrorMessage(result.message || 'Failed to start quiz. Please try again.');
                setShowErrorModal(true);
            }
        } catch (error) {
            console.error('Error starting quiz:', error);
            setErrorMessage('Network error. Please check your connection and try again.');
            setShowErrorModal(true);
        } finally {
            setStartingQuiz(false);
        }
    };

    const handleCloseModal = () => {
        navigate('/trivia-games');
    };

    const handleCloseCompletionModal = () => {
        navigate('/trivia-games');
    };

    const handleTryAgain = () => {
        if (!canAccess) {
            navigate('/trivia-games');
            return;
        }
        
        setShowTimeoutModal(false);
        setShowCompletionModal(false);
        setTimeExpired(false);
        setCurrentIndex(0);
        setSteps([]);
        setBatchId(null);
        setSessionId(null);
        setPuzzle(null);
        
        // Reset time to 90 seconds (1 min 30 secs)
        setTimeLeft(90);
        
        setShowModal(true);
        setQuizStarted(false);
    };

    // Check if current question is the last one (index 3 for the 4th question)
    const isLastQuestion = currentIndex >= 3;

    return (
        <div className='w-full flex items-start justify-center bg-[#F9FAFB] relative'>
            <div className='flex flex-col w-full '>
                <div>
                    <Header 
                        title='Health Chain Reaction'
                    />  
                </div>

                <div 
                    className='min-h-screen p-4 bg-cover bg-center bg-no-repeat flex items-center justify-center'
                    style={{ backgroundImage: `url(${BackgroundImage})` }}
                >
                    {loading ? (
                        <div className='w-full max-w-xl px-4'>
                            <div className='bg-[#308042B2]/70 rounded-lg p-6 md:p-8 shadow-xl text-center'>
                                <div className='text-white text-lg instrument-sans-medium'>
                                    Loading...
                                </div>
                            </div>
                        </div>
                    ) : showModal ? (
                        <div className='w-full max-w-xl px-4'>
                            <div className='bg-[#308042B2]/70 rounded-lg p-6 md:p-8 shadow-xl relative'>
                                <button
                                    onClick={handleCloseModal}
                                    className='absolute top-4 right-4 text-white hover:text-gray-300 transition-colors'
                                >
                                    <IoClose className='text-2xl' />
                                </button>

                                <div className='text-center mb-6 mt-4'>
                                    <h1 className='text-xl md:text-[32px] instrument-sans-bold text-white'>
                                        Health Chain Reaction
                                    </h1>
                                </div>

                                <div className='mb-4'>
                                    <p className='text-white text-center text-sm md:text-base instrument-sans-normal'>
                                        Arrange health actions in the correct order.
                                        <br />
                                        <span className='font-bold mt-2 block'>You have 1 minute 30 seconds to complete the quiz!</span>
                                    </p>
                                </div>

                                {accessMessage && (
                                    <div className='mb-4'>
                                        <p className='text-white text-center text-xs md:text-sm instrument-sans-medium'>
                                            {accessMessage}
                                        </p>
                                    </div>
                                )}

                                {loading ? (
                                    <div className='mb-8'>
                                        <div className='flex flex-col items-center gap-2'>
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                                            <p className='text-white text-center text-sm md:text-base instrument-sans-semibold'>
                                                Loading game configuration...
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className='mb-8'>
                                        <p className='text-white text-center text-sm md:text-base instrument-sans-semibold'>
                                            Note: You have {attemptsLeft} attempt{attemptsLeft !== 1 ? 's' : ''} left.
                                        </p>
                                    </div>
                                )}

                                <div className='flex justify-center'>
                                    <button
                                        onClick={handlePlay}
                                        disabled={!canAccess || loading || startingQuiz}
                                        className='bg-[#069125] hover:bg-white hover:text-black text-white py-3 px-12 rounded-lg transition-colors duration-200 instrument-sans-semibold w-full disabled:opacity-50 disabled:cursor-not-allowed'
                                    >
                                        {startingQuiz ? 'Starting...' : loading ? 'Loading...' : 'Play'}
                                    </button>
                                </div>

                                {showErrorModal && (
                                    <div className='mt-4'>
                                        <p className='text-red-300 text-center text-sm md:text-base instrument-sans-normal mb-2'>
                                            {errorMessage}
                                        </p>
                                        <p className='text-white/70 text-center text-xs instrument-sans-normal'>
                                            Redirecting to games page...
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : !showCompletionModal && !showTimeoutModal ? (
                        puzzle && steps.length > 0 ? (
                            <div className='w-full max-w-4xl px-4'>
                                <div className='bg-[#308042B2]/70 rounded-lg p-6 md:p-8 shadow-xl'>
                                    {/* Timer and Question Progress */}
                                    <div className='flex flex-col items-center mb-6'>
                                        <div className='flex items-center gap-2 text-white mb-2'>
                                            <AiOutlineClockCircle className='text-xl' />
                                            <span className='text-base md:text-lg instrument-sans-bold'>
                                                {formatTime(timeLeft)}
                                            </span>
                                        </div>
                                        {batchId && (
                                            <p className='text-white/60 text-xs'>
                                                Batch ID: {batchId}
                                            </p>
                                        )}
                                    </div>

                                    {/* Question Text */}
                                    <div className='mb-8 flex flex-col items-center gap-2'>
                                        <p className='text-base md:text-lg instrument-sans-bold text-white'>
                                            Question {currentIndex + 1}/{totalQuestions}
                                        </p>

                                        {puzzle.category && (
                                            <p className='text-white/80 text-sm instrument-sans-medium'>
                                                {puzzle.category}
                                            </p>
                                        )}

                                        <h2 className='text-white text-lg md:text-[24px] text-center instrument-sans-bold'>
                                            {puzzle.description || 'Arrange the steps in the correct order.'}
                                        </h2>
                                    </div>

                                    {/* Steps to Order */}
                                    <div className='mb-6 space-y-3'>
                                        {steps.map((step, index) => (
                                            <div
                                                key={step.id}
                                                draggable
                                                onDragStart={() => handleDragStart(index)}
                                                onDragOver={handleDragOver}
                                                onDrop={() => handleDrop(index)}
                                                className='bg-white/20 text-white border border-white rounded-lg p-4 flex items-center gap-3 cursor-move hover:bg-white/30 transition-colors'
                                            >
                                                <MdDragIndicator className='text-2xl text-white/60 flex-shrink-0' />
                                                <div className='flex-1'>
                                                    <div className='flex items-center gap-2 mb-1'>
                                                        <span className='text-sm font-semibold text-white/80'>
                                                            Step {index + 1}
                                                        </span>
                                                    </div>
                                                    <p className='instrument-sans-medium text-white'>
                                                        {step.text}
                                                    </p>
                                                </div>
                                                <div className='flex flex-col gap-1'>
                                                    <button
                                                        onClick={() => handleMoveUp(index)}
                                                        disabled={index === 0}
                                                        className='text-white/80 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed'
                                                        title='Move up'
                                                    >
                                                        ↑
                                                    </button>
                                                    <button
                                                        onClick={() => handleMoveDown(index)}
                                                        disabled={index === steps.length - 1}
                                                        className='text-white/80 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed'
                                                        title='Move down'
                                                    >
                                                        ↓
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Submit Button */}
                                    <div className='flex justify-end'>
                                        <button
                                            onClick={handleSubmit}
                                            disabled={submittingAnswer}
                                            className='bg-[#069125] hover:bg-[#057a1f] text-white instrument-sans-medium py-3 px-8 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
                                        >
                                            {submittingAnswer ? 'Submitting...' : isLastQuestion ? 'Finish and Submit' : 'Submit answer'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className='w-full max-w-xl px-4'>
                                <div className='bg-[#308042B2]/70 rounded-lg p-6 md:p-8 shadow-xl text-center text-white'>
                                    <p>Loading question...</p>
                                </div>
                            </div>
                        )
                    ) : null}
                </div>
            </div>

            {/* Completion Modal */}
            {showCompletionModal && (
                <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
                    <div className='w-full max-w-xl px-4'>
                        <div className='bg-[#308042B2]/70 rounded-lg p-6 md:p-8 shadow-xl relative'>
                            <div className='text-center mb-6 mt-4'>
                                <h1 className='text-xl md:text-[32px] instrument-sans-bold text-white'>
                                    Quiz Completed!
                                </h1>
                            </div>

                            <div className='mb-6'>
                                <p className='text-white text-center text-sm md:text-base instrument-sans-normal'>
                                    {completionMessage}
                                </p>
                            </div>

                            <div className='flex justify-center'>
                                <button
                                    onClick={handleCloseCompletionModal}
                                    className='bg-[#069125] hover:bg-white hover:text-black text-white py-3 px-12 rounded-lg transition-colors duration-200 instrument-sans-semibold w-full'
                                >
                                    Back to Trivia Games
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Timeout Modal */}
            {showTimeoutModal && (
                <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
                    <div className='w-full max-w-xl px-4'>
                        <div className='bg-[#308042B2]/70 rounded-lg p-6 md:p-8 shadow-xl relative'>
                            <div className='text-center mb-6 mt-4'>
                                <h1 className='text-xl md:text-[32px] instrument-sans-bold text-white'>
                                    Time's Up!
                                </h1>
                            </div>

                            <div className='mb-8'>
                                <p className='text-white text-center text-sm md:text-base instrument-sans-normal'>
                                    Comiseration. you did not complete in time.
                                </p>
                            </div>

                            <div className='flex justify-center'>
                                <button
                                    onClick={handleTryAgain}
                                    disabled={!canAccess}
                                    className='bg-[#069125] hover:bg-white hover:text-black text-white py-3 px-12 rounded-lg transition-colors duration-200 instrument-sans-semibold w-full disabled:opacity-50 disabled:cursor-not-allowed'
                                >
                                    {canAccess ? 'Try Again' : 'Back to Trivia Games'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HealthChainReaction;